import isEqual from "lodash/isEqual";
import parsePhoneNumber, { CountryCode } from "libphonenumber-js";
import {
  AddressFragment,
  CheckoutSessionFragment,
  ContactInfo,
  GiftInfo,
  SavedShippingAddress,
  TenantQuery,
  UserAddressInput,
  VerifiedAddressFragment,
  VerifyAddressInput,
} from "v3/services/graphql/generated";
import { StatesConfig, CountriesAllowed } from "services/graphql/generated";
import {
  AutocompletedAddressValues,
  ReactSelectDropdownOption,
} from "@pepdirect/v3/types";
import { unitedStatesOption } from "v3/constants/address";
import { AddressDetailsFormValues } from "v3/types/address";
import { SuggestedAddress } from "v3/types/error";

interface IsEnteredAndVerifiedAddressSame {
  enteredAddress: VerifyAddressInput;
  isStateRequired: boolean;
  verifiedAddress: VerifiedAddressFragment;
}

export const isEnteredAndVerifiedAddressSame = ({
  enteredAddress,
  isStateRequired,
  verifiedAddress,
}: IsEnteredAndVerifiedAddressSame): boolean => {
  const checkEquality = (left?: string | null, right?: string | null) => {
    // needed for falsy value comparison, ex: null vs. undefined vs. ""
    if (!left && !right) return true;
    return left?.toLowerCase() === right?.toLowerCase();
  };
  const isLine1Equal = checkEquality(
    enteredAddress.line1,
    verifiedAddress.line1
  );
  const isLine2Equal = checkEquality(
    enteredAddress.line2,
    verifiedAddress.line2
  );
  const isCityEqual = checkEquality(enteredAddress.city, verifiedAddress.city);
  const isStateEqual = isStateRequired
    ? checkEquality(enteredAddress.state, verifiedAddress.state)
    : true;
  const isZipEqual = checkEquality(enteredAddress.zip, verifiedAddress.zip);
  const isCountryEqual = checkEquality(
    enteredAddress.countryCode,
    verifiedAddress.countryCode
  );

  return (
    isLine1Equal &&
    isLine2Equal &&
    isCityEqual &&
    isStateEqual &&
    isZipEqual &&
    isCountryEqual
  );
};

// convert form values to AddressVerificationInput
// type for address verification
export const formatShipToAddrVerifInput = ({
  ship,
  isStateRequired,
}: {
  ship: AddressDetailsFormValues;
  isStateRequired: boolean;
}): VerifyAddressInput => {
  return {
    city: ship.city,
    company: ship.company || null,
    countryCode: ship.country?.value,
    firstName: ship.firstName,
    lastName: ship.lastName,
    line1: ship.line1,
    line2: ship.line2 || null,
    ...(isStateRequired ? { state: ship.state?.value } : {}),
    zip: ship.zip,
  };
};

export type VerificationAddrTypes = AddressDetailsFormValues | SuggestedAddress;

interface UpdateEnteredAddressWithSuggestedAddressArgs {
  enteredAddress: AddressDetailsFormValues;
  suggestedAddress: SuggestedAddress;
}

// combine the suggested address with fields from form that are not suggested,
// and also convert phone to phoneNumber because that is what the input requires
// on add/edit address
export const updateEnteredAddressWithSuggestedAddress = ({
  enteredAddress,
  suggestedAddress,
}: UpdateEnteredAddressWithSuggestedAddressArgs): UserAddressInput => {
  const { city, countryCode, line1, line2, state, zip } = suggestedAddress;
  const {
    company,
    firstName,
    isDefault,
    lastName,
    nickname,
    phone,
    specialInstructions,
  } = enteredAddress;

  return {
    city,
    company,
    countryCode,
    firstName,
    lastName,
    line1,
    line2,
    state,
    zip,
    isDefault,
    nickname,
    phoneNumber: phone,
    specialInstructions,
  };
};

// Convert form values to UserAddressInput needed for create/update user address.
export const getUserAddressInputFromFormValues = (
  formValues: AddressDetailsFormValues
): UserAddressInput => {
  const {
    firstName,
    lastName,
    company,
    line1,
    line2,
    city,
    state,
    country,
    zip,
    phone,
    nickname,
    specialInstructions,
    isDefault,
  } = formValues;

  return {
    firstName,
    lastName,
    company,
    line1,
    line2,
    city,
    zip,
    phoneNumber: phone,
    nickname,
    specialInstructions,
    isDefault,
    state: state?.value,
    countryCode: country.value,
  };
};

interface IsShipVerifiedArgs {
  tenant: TenantQuery["tenant"];
  shipSelectedFromAutocomplete: AutocompletedAddressValues | null;
  newShip?: Partial<AddressDetailsFormValues>;
  savedShip?: AddressFragment | null;
}

export const determineIfShipVerified = ({
  tenant,
  shipSelectedFromAutocomplete,
  newShip,
  savedShip,
}: IsShipVerifiedArgs) => {
  const isValidationOptional =
    tenant?.contactConfig.shippingAddressConfig.requireValidation ===
    "OPTIONAL";

  // a saved ship is already verified if validation is optional
  // if validation NOT optional, we want to run verification on the saved ship
  if (savedShip) return isValidationOptional;

  // PCS-1014 only verify ship with Loqate once (don't Find/Retrieve then Verify)
  // if a user chooses an autocompleted address and doesn't change it,
  // it has already been verified by the Loqate Find and Retrieve APIs
  if (shipSelectedFromAutocomplete && newShip) {
    const { line1, line2, city, state, zip, country } = newShip;
    const formattedNewShip: AutocompletedAddressValues = {
      line1: line1 || "",
      line2: line2 || "",
      city: city || "",
      state: state?.value || "",
      zip: zip || "",
      country: country?.value || "",
    };
    if (isEqual(shipSelectedFromAutocomplete, formattedNewShip)) return true;
  }

  return false;
};

interface IsEnteredAddressValidated {
  autocompletedAddress: AutocompletedAddressValues | null;
  checkoutSessionAddress?: NonNullable<
    CheckoutSessionFragment["contactInfo"]
  >["shippingAddress"];
  enteredAddress: AddressDetailsFormValues;
  isStateRequired?: boolean;
}

export const isEnteredAddressValidated = ({
  autocompletedAddress,
  checkoutSessionAddress,
  enteredAddress,
  isStateRequired = true,
}: IsEnteredAddressValidated) => {
  // format all the addresses for comparison:
  // - convert to lower case
  // - convert to empty strings if falsy
  // - remove state if !isStateRequired because Loqate returns it even for Polish addresses
  const formattedCheckoutSessionAddress = {
    city: checkoutSessionAddress?.city?.toLowerCase() || "",
    country: checkoutSessionAddress?.countryCode?.toLowerCase() || "",
    line1: checkoutSessionAddress?.line1?.toLowerCase() || "",
    line2: checkoutSessionAddress?.line2?.toLowerCase() || "",
    ...(isStateRequired
      ? { state: checkoutSessionAddress?.state?.toLowerCase() || "" }
      : {}),
    zip: checkoutSessionAddress?.zip?.toLowerCase() || "",
  };
  const formattedEnteredAddress = {
    city: enteredAddress.city.toLowerCase() || "",
    country: enteredAddress.country.value.toLowerCase() || "",
    line1: enteredAddress.line1.toLowerCase() || "",
    line2: enteredAddress.line2?.toLowerCase() || "",
    ...(isStateRequired
      ? { state: enteredAddress.state?.value.toLowerCase() || "" }
      : {}),
    zip: enteredAddress.zip.toLowerCase() || "",
  };
  const formattedAutocompletedAddress = {
    city: autocompletedAddress?.city.toLowerCase() || "",
    country: autocompletedAddress?.country.toLowerCase() || "",
    line1: autocompletedAddress?.line1.toLowerCase() || "",
    line2: autocompletedAddress?.line2.toLowerCase() || "",
    ...(isStateRequired
      ? { state: autocompletedAddress?.state.toLowerCase() || "" }
      : {}),
    zip: autocompletedAddress?.zip.toLowerCase() || "",
  };

  // if the entered address matches the checkoutSession address that is validated
  // or if it matches an existing autocompleted address, then it is validated
  const matchesValidatedCheckoutSessionAddress =
    checkoutSessionAddress?.isAddressValidated &&
    isEqual(formattedCheckoutSessionAddress, formattedEnteredAddress);
  const matchesAutocompletedAddress = isEqual(
    formattedAutocompletedAddress,
    formattedEnteredAddress
  );
  return matchesValidatedCheckoutSessionAddress || matchesAutocompletedAddress;
};

export const getStateReactSelectOption = (
  statesConfig: StatesConfig[],
  countryValue: string,
  state: string
): ReactSelectDropdownOption | null => {
  const states = getStates(statesConfig, countryValue);
  return (states && states.find((st) => st.value === state)) || null;
};

export const getCountryReactSelectOption = (
  countriesAllowed: CountriesAllowed[],
  countryCode: string
): ReactSelectDropdownOption => {
  const countries = getCountries(countriesAllowed);
  return (
    (countries && countries.find((co) => co.value === countryCode)) ||
    unitedStatesOption
  );
};

export const getStates = (
  statesConfig: StatesConfig[],
  countryCode = "US",
  selectedCountry?: ReactSelectDropdownOption
): ReactSelectDropdownOption[] => {
  if (!statesConfig) {
    return [];
  }
  const states = statesConfig.find(
    (config: StatesConfig) =>
      config.countryId === (selectedCountry?.value || countryCode || "US")
  );
  return states
    ? states.statesAllowed.map((state) => {
        return { value: state.id, label: state.id };
      })
    : [];
};

export const getCountries = (
  countries?: CountriesAllowed[]
): ReactSelectDropdownOption[] => {
  if (!countries) {
    return [];
  }
  return countries.map((country: CountriesAllowed) => ({
    value: country.id,
    label: country.name,
  }));
};

export const getCountryDropdownOption = (
  countryCode: string,
  countries?: CountriesAllowed[]
): ReactSelectDropdownOption | null => {
  const countriesAllowed = getCountries(countries);
  return (
    countriesAllowed.find((country) => country.value === countryCode) || null
  );
};

export const getInitialValuesShippingStep = ({
  contactInfo,
  countriesAllowed,
  countryOnLoad,
  giftInfo,
  statesConfig,
}: {
  contactInfo?: ContactInfo | null;
  countriesAllowed: CountriesAllowed[];
  countryOnLoad: string;
  giftInfo?: GiftInfo | null;
  statesConfig: StatesConfig[];
}) => {
  const countryValue =
    countryOnLoad &&
    getCountryReactSelectOption(countriesAllowed, countryOnLoad);

  const isSavedAddress = !!contactInfo?.shippingAddress?.addressId;
  // if no contact info OR it's a saved address AND there's no gift info,
  // there's nothing on the form, but always pre-populate the country on load
  if ((!contactInfo || isSavedAddress) && !giftInfo)
    return {
      shippingAddress: { country: countryValue || unitedStatesOption },
    };

  const { email, shippingAddress, phone } = contactInfo || {};
  const {
    city,
    company,
    countryCode,
    firstName,
    lastName,
    line1,
    line2,
    nickname,
    specialInstructions,
    state,
    taxId,
    zip,
  } = shippingAddress || {};

  const stateValue =
    countryOnLoad &&
    state &&
    getStateReactSelectOption(statesConfig, countryOnLoad, state);
  const parsedNumber =
    phone && parsePhoneNumber(phone, (countryCode as CountryCode) || "US");
  return {
    shippingAddress: {
      city,
      country: countryValue || unitedStatesOption,
      email,
      firstName,
      lastName,
      line1,
      line2,
      nickname,
      phone: parsedNumber && parsedNumber.nationalNumber,
      specialInstructions,
      state: stateValue,
      zip,
      company,
      taxId,
    },
    ...(giftInfo && { giftInfo }),
  };
};

export const sortAddressesByDefault = (addresses: SavedShippingAddress[]) => {
  return addresses.sort((a, b) => {
    if (a.isDefault) return -1;
    if (b.isDefault) return 1;
    return 0;
  });
};

const isLoggedInWithFormValues = ({
  contactInfo,
  isGuest,
}: {
  contactInfo?: ContactInfo | null;
  isGuest: boolean;
}): boolean => {
  const { phone, shippingAddress } = contactInfo || {};
  // if there's phone or ship values and no id, then the values came from the form
  return (
    !isGuest && !!(phone || shippingAddress) && !shippingAddress?.addressId
  );
};

export const getSelectedAddress = ({
  addresses,
  contactInfo,
  isGuest,
}: {
  addresses: SavedShippingAddress[];
  contactInfo?: ContactInfo | null;
  isGuest: boolean;
}): string | null => {
  const { addressId } = contactInfo?.shippingAddress || {};
  // if contact info already has an address id, select that one
  if (addressId) return addressId;
  // pre-select first address if logged in, has saved addresses, and hasn't filled out form
  return !isGuest &&
    addresses.length &&
    !isLoggedInWithFormValues({ contactInfo, isGuest })
    ? addresses[0].addressId
    : null;
};

export const showShipFormOnLoad = ({
  addresses,
  contactInfo,
  isGuest,
}: {
  addresses: SavedShippingAddress[];
  contactInfo?: ContactInfo | null;
  isGuest: boolean;
}): boolean => {
  // always show ship form for guest, and users with no saved addresses
  if (isGuest || !addresses.length) return true;
  return isLoggedInWithFormValues({ contactInfo, isGuest });
};

export const convertAddressFromFormToInput = (
  addr: AddressDetailsFormValues
) => {
  const { country, city, firstName, lastName, line1, line2, state, zip } = addr;
  return {
    city,
    firstName,
    lastName,
    line1,
    line2,
    zip,
    countryCode: country.value,
    state: state?.value,
  };
};

export const getInitialFormValuesEditAddress = (
  address: AddressFragment | null,
  countriesAllowed: CountriesAllowed[],
  statesConfig: StatesConfig[],
  countryOnLoad: string
) => {
  const countryValue = getCountryReactSelectOption(
    countriesAllowed,
    countryOnLoad
  );

  if (!address)
    return {
      shippingAddress: { country: countryValue || unitedStatesOption },
    };

  const {
    firstName,
    lastName,
    company,
    line1,
    line2,
    city,
    state,
    zip,
    countryCode,
    phoneNumber,
    nickname,
    specialInstructions,
    isDefault,
  } = address;

  const parsedNumber =
    phoneNumber &&
    parsePhoneNumber(phoneNumber, (countryCode as CountryCode) || "US");

  const stateValue =
    state && getStateReactSelectOption(statesConfig, countryCode, state);
  return {
    shippingAddress: {
      isDefault,
      firstName,
      lastName,
      company,
      line1,
      line2,
      city,
      state: stateValue,
      zip,
      country: countryValue,
      phone: parsedNumber && parsedNumber.nationalNumber,
      phoneNumber: parsedNumber && parsedNumber.nationalNumber,
      ...(nickname && { nickname }),
      ...(specialInstructions && { specialInstructions }),
    },
  };
};
